<template>
    <div
        v-bind="$attrs"
        :id="$style.map"
        ref="mapElement"
        :style="`width: 100%; height: ${height}px;`"
    />
    <component
        :is="popupInfo.popup.component"
        v-for="popupInfo in componentPopups"
        v-show="false"
        :id="popupInfo.id"
        :key="popupInfo.id"
        ref="popupElements"
        v-bind="popupInfo.popup.props"
    />
</template>

<script setup lang="ts">
import L from 'leaflet'
import 'leaflet.markercluster'
import 'leaflet/dist/leaflet.css'

type ComponentPopup = {
    component: Component
    props: Record<string, unknown>
}
export type Marker = {
    id: string | number
    location: L.LatLngExpression
    icon: string
    iconClass: string
    popup?: string | ComponentPopup
    popupAnchor?: [number, number]
    onClick(): void
}
export type AppMapProps = {
    height: number
    markers?: Array<Marker>
    selectMarker?: Marker['id']
}
type MapMarker = {
    id: string | number
    marker: L.Marker
    onClick: () => void
}
interface PopupInfo {
    id: string | number
    anchor: Marker['popupAnchor']
}
interface StringPopupInfo extends PopupInfo {
    popup: string
}
interface ComponentPopupInfo extends PopupInfo {
    popup: ComponentPopup
}

const props = defineProps<AppMapProps>()
const isComponentPopupInfo = (popup: StringPopupInfo | ComponentPopupInfo): popup is ComponentPopupInfo => typeof popup.popup !== 'string'
const mapElement = ref<HTMLDivElement>()
const popupElements = ref<ComponentPublicInstance[]>([])

const mapMarkers = computed<MapMarker[]>(() => {
    return props.markers?.map((marker: Marker) => {
        const leafletMarker = L.marker(marker.location, {
            icon: L.divIcon({
                className: marker.iconClass,
                html: marker.icon,
                iconSize: [30, 30]
            })
        })

        return {
            id: marker.id,
            marker: leafletMarker,
            onClick: marker.onClick
        }
    }) ?? []
})

const popups = computed<Array<StringPopupInfo | ComponentPopupInfo>>(() => {
    return props.markers
        ?.filter((marker: Marker): marker is Marker & { popup: NonNullable<Marker['popup']> } => marker.popup != null)
        .map((marker) => ({
            id: marker.id,
            popup: marker.popup,
            anchor: marker.popupAnchor,
        } as StringPopupInfo | ComponentPopupInfo)) ?? []
})

const componentPopups = computed(() => popups.value.filter(isComponentPopupInfo))

watchEffect(() => {
    popupElements.value?.forEach(popupElement => {
        const el = popupElement.$el as HTMLElement
        const leafletMarker = mapMarkers.value.find(marker => String(marker.id) === el?.id)?.marker
        if (leafletMarker == null || el == null)
            return

        leafletMarker.bindPopup(el)
    })
})

watch(mapMarkers, () => {
    if (map == null) return

    map.eachLayer((layer: L.Layer) => {
        if (!(layer instanceof L.TileLayer)) {
            map!.removeLayer(layer)
        }
    })

    const clusterLayer = L.markerClusterGroup()
    mapMarkers.value.forEach((mapMarker) => {
        mapMarker.marker.on('click', () => {
            mapMarker.onClick()
        })
        clusterLayer.addLayer(mapMarker.marker)
    })
    map.addLayer(clusterLayer)

    const bounds = clusterLayer.getBounds()
    if (bounds.isValid()) {
        map.fitBounds(bounds)
    }
})

watch(() => props.selectMarker, (markerIndex: Marker['id'] | undefined) => {
    if (markerIndex == null) return

    mapMarkers.value.forEach((marker) => {
        marker.marker.closePopup()
    })

    if (map == null) return

    const index = Number(markerIndex)
    const selected = mapMarkers.value[index]
    if (selected == null) return

    map.setView(selected.marker.getLatLng(), 17)
    selected.marker.openPopup()
})

let map: L.Map | undefined

onMounted(() => {
    // Add little delay for rendering map, in case modal or parent is rendered with transition
    // so the width of the map is calculated correctly
    setTimeout(() => {
        if (mapElement.value == null) return
        map = L.map(mapElement.value).setView([51.5167, 9.9167], 6)
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map)
    }, 500)
})
</script>

<style lang="pcss" module>
:global {
    .leaflet-popup-content >* {
        display: block !important;
    }

    .marker-cluster-small {
        background-color: rgba(181, 226, 140, 0.6);
    }

    .marker-cluster-small div {
        background-color: rgba(110, 204, 57, 0.6);
    }

    .marker-cluster-medium {
        background-color: rgba(241, 211, 87, 0.6);
    }

    .marker-cluster-medium div {
        background-color: rgba(240, 194, 12, 0.6);
    }

    .marker-cluster-large {
        background-color: rgba(253, 156, 115, 0.6);
    }

    .marker-cluster-large div {
        background-color: rgba(241, 128, 23, 0.6);
    }

    .marker-cluster {
        background-clip: padding-box;
        border-radius: 20px;
    }

    .marker-cluster div {
        width: 30px;
        height: 30px;
        margin-left: 5px;
        margin-top: 5px;

        text-align: center;
        border-radius: 15px;
        font: 12px "Helvetica Neue", Arial, Helvetica, sans-serif;
    }

    .marker-cluster span {
        line-height: 30px;
    }

    .leaflet-cluster-anim .leaflet-marker-icon, .leaflet-cluster-anim .leaflet-marker-shadow {
        -webkit-transition: -webkit-transform 0.3s ease-out, opacity 0.3s ease-in;
        -moz-transition: -moz-transform 0.3s ease-out, opacity 0.3s ease-in;
        -o-transition: -o-transform 0.3s ease-out, opacity 0.3s ease-in;
        transition: transform 0.3s ease-out, opacity 0.3s ease-in;
    }

    .leaflet-cluster-spider-leg {
        /* stroke-dashoffset (duration and function) should match with leaflet-marker-icon transform in order to track it exactly */
        -webkit-transition: -webkit-stroke-dashoffset 0.3s ease-out, -webkit-stroke-opacity 0.3s ease-in;
        -moz-transition: -moz-stroke-dashoffset 0.3s ease-out, -moz-stroke-opacity 0.3s ease-in;
        -o-transition: -o-stroke-dashoffset 0.3s ease-out, -o-stroke-opacity 0.3s ease-in;
        transition: stroke-dashoffset 0.3s ease-out, stroke-opacity 0.3s ease-in;
    }
}
</style>
